import './AddInvoiceRowModal.scss';
import cn from 'classnames';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Form, InputGroup, Modal } from 'react-bootstrap';
import {
  IncludeInFeeCalc,
  IncludeInFeeCalcType,
  InvoiceRow,
  ManualEntry,
  ManualEntryType,
  ManualInvoiceOptions,
  OUT_OF_APP_ACCOUNT_ID,
  OUT_OF_APP_ACCOUNT_NAME,
  REDSHIFT_TVAD_SOURCES,
  RedshiftTVADSource,
  YOUTUBE_CHANNEL
} from '@blisspointmedia/bpm-types/dist/BillingAutomation';
import { Button } from '@Components/Button';
import { Appearance, Design, Placement, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { MenuItem } from '@Components/Dropdown/dropdown-constants';
import { EditableDropdown, EditableDropdownItem } from '@Components/EditableDropdown';
import { RenameModal } from '@Components/RenameModal/RenameModal';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { Spinner } from '@Components/Spinner';
import {
  getSection,
  MANUAL_ENTRY_TYPE_TO_PRETTY_NAME,
  MONTH_OPTION_LOOKBACK,
  recentMonthOptions,
  SECTION_NON_TVAD,
  SECTION_TVAD,
  SECTION_YOUTUBE,
  TVAD_FILTER_PILLS
} from './billing-utils';

// Stable empty fallback so memoized option lists below have a consistent reference while
// `options` is still loading (the modal opens immediately; see resolvedOptions).
const EMPTY_OPTIONS: ManualInvoiceOptions = {
  relationships: [],
  billingCategories: [],
  channels: [],
  platforms: []
};

interface AddInvoiceRowModalProps {
  show: boolean;
  onClose: () => void;
  onSave: (row: InvoiceRow) => void;
  // undefined while options are still loading -- the modal opens immediately and shows a
  // spinner rather than waiting on the caller to gate the button/modal on this being ready.
  options?: ManualInvoiceOptions;
  billingMonth: string;
  existingRow?: InvoiceRow;
  // Freehand out-of-app platforms typed via the "Add Platform" flow. Lifted to the parent so
  // they persist across multiple add-row sessions, preventing near-duplicate platform names
  // (e.g. "TikTok" vs "Tik Tok") from being typed twice.
  addedPlatformOptions: string[];
  setAddedPlatformOptions: Dispatch<SetStateAction<string[]>>;
}

// SegmentedControlButton's generic is `T extends string`, so the Billable toggle can't use
// `boolean` keys directly — use string keys and translate to/from the boolean state.
const BillableOption = {
  YES: 'yes',
  NO: 'no'
} as const;
type BillableOptionType = (typeof BillableOption)[keyof typeof BillableOption];

// The Dropdown's option value can't be null, so "no category" uses a sentinel mapped back to null on save.
const NA_CATEGORY = '__na__';

// Mirrors AccountSelect.tsx's getSection usage, applied to a plain channel string
// instead of a BillingAccount.
const getSectionByChannel = (channel: string): string =>
  getSection(channel === YOUTUBE_CHANNEL, REDSHIFT_TVAD_SOURCES.has(channel as RedshiftTVADSource));

export const AddInvoiceRowModal = ({
  show,
  onClose,
  onSave,
  options,
  billingMonth,
  existingRow,
  addedPlatformOptions,
  setAddedPlatformOptions
}: AddInvoiceRowModalProps) => {
  const isEdit = existingRow !== undefined;
  const [entryType, setEntryType] = useState<ManualEntryType>(
    existingRow?.manual?.entryType ?? ManualEntry.CORRECTION
  );
  const [relId, setRelId] = useState(existingRow?.salesforceRelationshipId ?? '');
  const [accountId, setAccountId] = useState(existingRow?.accountId ?? '');
  const [channel, setChannel] = useState(existingRow?.channel ?? '');
  const [platform, setPlatform] = useState(existingRow?.platform ?? '');
  const [category, setCategory] = useState<string>(existingRow?.billingCategory ?? NA_CATEGORY);
  const [amount, setAmount] = useState<string>(existingRow ? String(existingRow.metricToBill) : '');
  const [billable, setBillable] = useState(existingRow?.billable ?? false);
  const [includeInFeeCalc, setIncludeInFeeCalc] = useState<IncludeInFeeCalcType>(
    existingRow?.includeInFeeCalc ?? IncludeInFeeCalc.NO
  );
  const [note, setNote] = useState(existingRow?.manual?.note ?? '');
  const [correctionMonth, setCorrectionMonth] = useState(
    existingRow?.manual?.correctionMonth ?? ''
  );
  const [showAddPlatformModal, setShowAddPlatformModal] = useState(false);

  const isCorrection = entryType === ManualEntry.CORRECTION;
  const isOutOfApp = entryType === ManualEntry.OUT_OF_APP;

  const resolvedOptions = options ?? EMPTY_OPTIONS;

  // Corrections: only relationships that have mapped accounts. Out-of-app: all active relationships.
  const relationshipOptions = useMemo(
    () =>
      resolvedOptions.relationships
        .filter(r => (isOutOfApp ? r.active : r.accounts.length > 0))
        .map(r => ({ label: r.name, value: r.id })),
    [resolvedOptions.relationships, isOutOfApp]
  );

  const relationship = useMemo(
    () => resolvedOptions.relationships.find(r => r.id === relId),
    [resolvedOptions.relationships, relId]
  );
  const accounts = useMemo(() => relationship?.accounts ?? [], [relationship]);
  const selectedAccount = useMemo(
    () => accounts.find(a => a.account_id === accountId),
    [accounts, accountId]
  );

  // Out-of-app: flat company-wide list. Corrections: narrowed to the account's delivery channels
  // (which can equally include TVAD sources or YouTube for accounts delivering those). Both are
  // sectioned/pillable by getSectionByChannel.
  const channelMenuItems: MenuItem<string>[] = useMemo(() => {
    const channels = isOutOfApp
      ? resolvedOptions.channels
      : [...new Set((selectedAccount?.channelPlatforms ?? []).map(cp => cp.channel))];
    return channels.map(c => ({
      label: c,
      value: c,
      section: getSectionByChannel(c)
    }));
  }, [isOutOfApp, resolvedOptions.channels, selectedAccount]);

  // Out-of-app: flat company-wide list. Corrections: narrowed to the account+channel's delivery platforms.
  const fetchedPlatformOptions = useMemo(() => {
    if (isOutOfApp) {
      return resolvedOptions.platforms;
    }
    return [
      ...new Set(
        (selectedAccount?.channelPlatforms ?? [])
          .filter(cp => cp.channel === channel)
          .map(cp => cp.platform)
      )
    ];
  }, [isOutOfApp, resolvedOptions.platforms, selectedAccount, channel]);

  // Downstream fields can't be meaningfully chosen before their prerequisite -- out-of-app
  // channel/platform are flat company-wide lists with no such dependency.
  const accountReady = isOutOfApp || Boolean(relId);
  const channelReady = isOutOfApp || Boolean(accountId);
  const platformReady = isOutOfApp || Boolean(channel);

  const ooaPlatformItems: EditableDropdownItem[] = useMemo(
    () =>
      [...new Set([...fetchedPlatformOptions, ...addedPlatformOptions])].map((label, idx) => ({
        id: idx,
        label
      })),
    [fetchedPlatformOptions, addedPlatformOptions]
  );

  const numericAmount = Number(amount);
  const isValid =
    Boolean(relId) &&
    (isOutOfApp || Boolean(accountId)) &&
    Boolean(channel) &&
    Boolean(platform) &&
    Boolean(amount.trim()) &&
    !Number.isNaN(numericAmount) &&
    Boolean(note.trim()) &&
    (!isCorrection || Boolean(correctionMonth));

  const [y, m] = billingMonth.split('-');

  const handleSave = () => {
    if (!relationship) {
      return;
    }
    if (!isOutOfApp && !selectedAccount) {
      return;
    }
    onSave({
      calculationId: existingRow?.calculationId ?? '',
      reportYear: Number(y),
      reportMonth: Number(m),
      accountId: isOutOfApp ? OUT_OF_APP_ACCOUNT_ID : selectedAccount!.account_id,
      accountName: isOutOfApp ? OUT_OF_APP_ACCOUNT_NAME : selectedAccount!.name,
      salesforceRelationshipId: relationship.id,
      salesforceRelationshipName: relationship.name,
      billingCategory: category === NA_CATEGORY ? null : category,
      channel,
      platform,
      metricToBill: numericAmount,
      billable,
      includeInFeeCalc,
      signedOffBy: null,
      signedOffAt: null,
      billingMetric: null,
      exported: false,
      manual: {
        entryType,
        note: note.trim(),
        correctionMonth: isCorrection ? correctionMonth : null
      }
    });
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      className='add-invoice-row-modal'
      centered
      enforceFocus={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit Manual Invoice Row' : 'Add Manual Invoice Row'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!options ? (
          <div className='add-invoice-row-modal-loading'>
            <Spinner />
          </div>
        ) : (
          <>
            <div className='field'>
              <span className='field-label'>Source</span>
              <SegmentedControlButton<ManualEntryType>
                size={Size.LG}
                isExclusiveSelect
                selectedOption={entryType}
                onChange={next => {
                  setEntryType(next);
                  setChannel('');
                  setPlatform('');

                  const nextIsOutOfApp = next === ManualEntry.OUT_OF_APP;
                  const currentRelationship = resolvedOptions.relationships.find(
                    r => r.id === relId
                  );
                  const relationshipStillValid =
                    currentRelationship &&
                    (nextIsOutOfApp
                      ? currentRelationship.active
                      : currentRelationship.accounts.length > 0);

                  if (nextIsOutOfApp || !relationshipStillValid) {
                    setAccountId('');
                  }
                  if (!relationshipStillValid) {
                    setRelId('');
                  }
                }}
                options={[
                  {
                    key: ManualEntry.CORRECTION,
                    label: MANUAL_ENTRY_TYPE_TO_PRETTY_NAME[ManualEntry.CORRECTION]
                  },
                  {
                    key: ManualEntry.OUT_OF_APP,
                    label: MANUAL_ENTRY_TYPE_TO_PRETTY_NAME[ManualEntry.OUT_OF_APP]
                  }
                ]}
              />
              <span className='field-hint'>
                A correction that reduces a previous invoice is entered as a negative amount;
                out-of-app spend is normally positive.
              </span>
            </div>

            <div className='fields-grid'>
              {isCorrection && (
                <div className='field'>
                  <span className='field-label'>
                    Month corrected
                    <span className='billing-asterisk'>*</span>
                  </span>
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    size={Size.LG}
                    label='Month'
                    value={correctionMonth}
                    options={recentMonthOptions(MONTH_OPTION_LOOKBACK, {
                      anchor: billingMonth,
                      startOffset: 1
                    })}
                    onChange={setCorrectionMonth}
                  />
                </div>
              )}

              <div className='field'>
                <span className='field-label'>
                  Relationship
                  <span className='billing-asterisk'>*</span>
                </span>
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  size={Size.LG}
                  label='Relationship'
                  value={relId}
                  options={relationshipOptions}
                  onChange={v => {
                    setRelId(v);
                    setAccountId('');
                    setChannel('');
                    setPlatform('');
                  }}
                />
              </div>

              {!isOutOfApp && (
                <div className='field'>
                  <span className='field-label'>
                    Account
                    <span className='billing-asterisk'>*</span>
                  </span>
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    size={Size.LG}
                    label='Account'
                    value={accountId}
                    options={accounts.map(a => ({ label: a.name, value: a.account_id }))}
                    onChange={v => {
                      setAccountId(v);
                      setChannel('');
                      setPlatform('');
                    }}
                    disabled={!accountReady}
                  />
                  <span className={cn('field-hint', 'required-hint', { hidden: accountReady })}>
                    Select a Relationship first.
                  </span>
                </div>
              )}

              <div className='field'>
                <span className='field-label'>
                  Channel
                  <span className='billing-asterisk'>*</span>
                </span>
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  size={Size.LG}
                  label='Channel'
                  value={channel}
                  menuItems={channelMenuItems}
                  section={[
                    { title: SECTION_TVAD },
                    { title: SECTION_YOUTUBE },
                    { title: SECTION_NON_TVAD }
                  ]}
                  filterPills={TVAD_FILTER_PILLS}
                  onChange={v => {
                    setChannel(v);
                    setPlatform('');
                  }}
                  disabled={!channelReady}
                />
                <span className={cn('field-hint', 'required-hint', { hidden: channelReady })}>
                  Select a Relationship & Account first.
                </span>
              </div>

              <div className='field'>
                <span className='field-label'>
                  Platform
                  <span className='billing-asterisk'>*</span>
                </span>
                {isOutOfApp ? (
                  <EditableDropdown
                    menuItems={ooaPlatformItems}
                    addItemText='Add Platform'
                    dropdownLabel='Select Platform'
                    addItemOnClick={() => setShowAddPlatformModal(true)}
                    selectedItem={ooaPlatformItems.find(item => item.label === platform)}
                    setSelectedItem={item => setPlatform(item?.label ?? '')}
                  />
                ) : (
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    size={Size.LG}
                    label='Platform'
                    value={platform}
                    options={fetchedPlatformOptions.map(p => ({ label: p, value: p }))}
                    onChange={setPlatform}
                    disabled={!platformReady}
                  />
                )}
                <span className={cn('field-hint', 'required-hint', { hidden: platformReady })}>
                  Select a Relationship, Account, & Channel first.
                </span>
              </div>

              {resolvedOptions.billingCategories.length > 0 && (
                <div className='field'>
                  <span className='field-label'>Billing Category</span>
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    size={Size.LG}
                    label='Billing Category'
                    value={category}
                    options={[
                      { label: 'N/A', value: NA_CATEGORY },
                      ...resolvedOptions.billingCategories.map(c => ({ label: c, value: c }))
                    ]}
                    onChange={setCategory}
                  />
                </div>
              )}

              <div className='field'>
                <span className='field-label'>
                  Amount
                  <span className='billing-asterisk'>*</span>
                </span>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    type='number'
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    placeholder='0.00'
                  />
                </InputGroup>
              </div>

              <div className='field'>
                <span className='field-label'>
                  Billable
                  <span className='billing-asterisk'>*</span>
                </span>
                <SegmentedControlButton<BillableOptionType>
                  size={Size.LG}
                  isExclusiveSelect
                  selectedOption={billable ? BillableOption.YES : BillableOption.NO}
                  onChange={v => setBillable(v === BillableOption.YES)}
                  options={[
                    { key: BillableOption.YES, label: 'Yes' },
                    { key: BillableOption.NO, label: 'No' }
                  ]}
                />
              </div>

              <div className='field'>
                <span className='field-label'>
                  Include in Fee Calc
                  <span className='billing-asterisk'>*</span>
                </span>
                <SegmentedControlButton<IncludeInFeeCalcType>
                  size={Size.LG}
                  isExclusiveSelect
                  selectedOption={includeInFeeCalc}
                  onChange={setIncludeInFeeCalc}
                  options={[
                    { key: IncludeInFeeCalc.YES, label: 'Yes' },
                    { key: IncludeInFeeCalc.NO, label: 'No' }
                  ]}
                />
              </div>
            </div>

            {showAddPlatformModal && (
              <RenameModal
                applyOnClick={label => {
                  const trimmed = label.trim();
                  if (trimmed) {
                    setPlatform(trimmed);
                    const isNewPlatform =
                      !fetchedPlatformOptions.includes(trimmed) &&
                      !addedPlatformOptions.includes(trimmed);
                    if (isNewPlatform) {
                      setAddedPlatformOptions(prev => [...prev, trimmed]);
                    }
                  }
                  setShowAddPlatformModal(false);
                }}
                applyText='Save'
                cancelText='Discard'
                onCancel={() => setShowAddPlatformModal(false)}
                onHide={() => setShowAddPlatformModal(false)}
                placeholder='New Platform Name'
                subTitle='New Platform Name'
                title='Add New Platform'
              />
            )}

            <div className='field'>
              <span className='field-label'>
                Note
                <span className='billing-asterisk'>*</span>
              </span>
              <Form.Control
                as='textarea'
                rows={2}
                value={note}
                onChange={e => setNote(e.target.value)}
              />
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!isValid} onClick={handleSave}>
          {isEdit ? 'Update' : 'Add'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
